import * as React from 'react';
import { type StepDefinition, type StepComponentProps } from './StepDefinition';
import { VStack, Text } from '@chakra-ui/react';
import { CatalogueFacade } from 'pos/facades';
import * as SalesStore from '../../SalesStore';
import { ProductForSaleDTO, Res, SoldProductType } from 'core/api';
import { StepLoader } from './StepLoader';
import { CompositionState, createCompositionStateFor } from './Composition';
import { useAutoAdvance } from './useStepAutoAdvance';
import { ScrollableGrid } from 'pos/ui';
import { ProductButton } from '../../sale/ProductButton';
import notesStep from './NotesStep';
import lotStep from './LotStep';
import removedIngredientsStep from './RemovedIngredientsStep';
import addinsStep from './AddinsStep';
import sizeAndColorStep from './SizeAndColorStep';
import { SessionProvider } from 'core/infrastructure';
import { Message } from 'core/ui-next/modal';


async function validate(state: CompositionState): Promise<boolean> {
	if (state.selectedProduct === null) {
		await Message.show(Res.Msg.MSG_MUST_SELECT_A_SALE_FORMAT());
		return false;
	}
	return true;
}

function clean(state: CompositionState): CompositionState {
	return { ...state, selectedProduct: null };
}

export const Component = (props: StepComponentProps) => {

	const { composition, setComposition, status, settings, requestNext, setSteps, buttonSize } = props;

	const { enableAutoAdvance } = useAutoAdvance({
		status,
		isCompleted: composition.selectedProduct !== null,
		onAutoAdvance: requestNext
	});

	const getData = React.useCallback(async (_composition: CompositionState) => {
		const formats =
			settings.type === SoldProductType.MenuItem
				? await CatalogueFacade.getSaleableAsMenuItemFormatsByProduct(
					settings.product.Id,
					SalesStore.getTicket().Id
				)
				: await CatalogueFacade.getSaleableAsMainFormatsByProduct(
					settings.product.Id,
					SalesStore.getTicket().Id
				);

		return { formats };
	}, [settings.product.Id, settings.type]);

	const onFormatSelected = React.useCallback(async (product: ProductForSaleDTO) => {

		if (product.SaleFormatId === composition.selectedProduct?.SaleFormatId) {
			enableAutoAdvance();
			return;
		}

		const result = await createCompositionStateFor(product, composition.quantity);
		if (result.ok) {
			setComposition(result.value);
			setSteps(getCompositionSteps(product, settings.product.HasMoreFormats), 'Format');
			enableAutoAdvance();
		}

	}, [composition.quantity, composition.selectedProduct?.SaleFormatId, enableAutoAdvance, setComposition, setSteps, settings.product.HasMoreFormats]);

	return <StepLoader {...props} getData={getData}>
		{({ formats }) => {
			const formatButtons = formats.map(format =>
				<ProductButton
					key={format.SaleFormatId}
					value={format}
					size={buttonSize}
					selected={format.SaleFormatId === composition.selectedProduct?.SaleFormatId}
					onClick={() => onFormatSelected(format)}
				/>
			);

			return <VStack width={'full'} height={'full'} gap={'4'}>
				<VStack width={'full'} height={'full'} gap={'4'} alignItems={'flex-start'}>
					<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.SELECT_SALE_FORMAT()}</Text>
					<ScrollableGrid items={formatButtons} buttonSize={buttonSize} columnGap={'2'} />
				</VStack>
			</VStack>;
		}}
	</StepLoader>;
};

const step: StepDefinition = {
	id: 'Format',
	canFinish: true,
	label: Res.Web.WIZARD_COMPOSE_SELECT_FORMAT_LBL(),
	useKeyPad: false,
	validate,
	clean,
	Component,
};

// Lo dejo en este fichero, porque sino tendríamos una dependencia circular
export function getCompositionSteps(product: ProductForSaleDTO, includeFormatStep?: boolean): StepDefinition[] {

	const steps: StepDefinition[] = [];

	// El step de Formatos, es especial, no debería desparecer nunca si ya existía.
	// Como los steps se recrean en base a formato seleccionado, cuando seleccionamos un formato,
	// este no tiene más formatos y no añadiría el step de formatos. El resto de steps, no nos 
	// importa que se recreen porque dependen del formato
	if (product.HasMoreFormats || includeFormatStep)
		steps.push(step);

	if (product.IsSoldBySizeAndColor)
		steps.push(sizeAndColorStep);

	if (product.AskForPreparationNotes)
		steps.push(notesStep);

	if (product.AskForAddins && product.HasAddins)
		steps.push(addinsStep);

	if (product.AdjustIngredientsOnSale)
		steps.push(removedIngredientsStep);

	if (SessionProvider.isTraceabilityEnabled() && product.HasLotControlOnSale)
		steps.push(lotStep);

	return steps;
}


export default step;



